import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Banknote, CalendarDays, DollarSign, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ChartCard, DataGrid, KpiCard, MetricStrip } from "@/components/dashboard";
import { formatCurrency, formatShortDate } from "@/lib/format";

type RevReservationRow = {
  id: number;
  guestName: string;
  apartmentName?: string;
  apartmentId?: number;
  checkIn?: string | null;
  checkOut?: string | null;
  totalPrice?: string | number;
  paidAmount?: string | number;
  paymentStatus?: string;
  source?: string;
};

type RevCommissionRow = {
  id: number;
  beneficiaryName?: string;
  beneficiaryId?: number;
  beneficiaryType?: string;
  amount?: string | number;
  percentage?: string | number;
  status: string;
};

export type RevReportData = {
  reservations: RevReservationRow[];
  financials: Array<Record<string, unknown>>;
  commissions: RevCommissionRow[];
  summary: {
    totalReservations: number;
    activeReservations: number;
    totalRevenue: string;
    totalCommissions: string;
    totalPaid: string;
    totalPending: string;
    bySource: Record<string, { count: number; revenue: number }>;
  };
};

export type RevenueTabProps = {
  revData?: RevReportData;
  apartmentMap: Record<number, string>;
  userMap: Record<number, string>;
};

function statusBadgeVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  if (status === "paid" || status === "completed") return "default";
  if (status === "pending") return "outline";
  if (status === "cancelled") return "destructive";
  return "secondary";
}

const SOURCE_COLORS = ["#6366f1", "#8b5cf6", "#22c55e", "#f59e0b", "#ef4444", "#0ea5e9"];

function dailyBuckets(records: Array<{ date?: string | null; value?: number }>, days = 30): { date: string; value: number }[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const buckets: Record<string, number> = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    buckets[d.toISOString().slice(0, 10)] = 0;
  }
  for (const r of records) {
    if (!r.date) continue;
    const key = new Date(r.date).toISOString().slice(0, 10);
    if (key in buckets) buckets[key] = (buckets[key] ?? 0) + (r.value ?? 0);
  }
  return Object.entries(buckets).map(([date, value]) => ({ date, value }));
}

function shortDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function RevenueTab({ revData, apartmentMap, userMap }: RevenueTabProps) {
  const summary = revData?.summary;
  const reservations = revData?.reservations ?? [];
  const commissions = revData?.commissions ?? [];

  // ---- Time-series buckets for sparklines + revenue trend ----
  const revenueSeries = useMemo(
    () => dailyBuckets(reservations.map(r => ({ date: r.checkIn, value: Number(r.totalPrice ?? 0) }))),
    [reservations],
  );
  const paidSeries = useMemo(
    () => dailyBuckets(reservations.map(r => ({ date: r.checkIn, value: Number(r.paidAmount ?? 0) }))),
    [reservations],
  );
  const reservationsSeries = useMemo(
    () => dailyBuckets(reservations.map(r => ({ date: r.checkIn, value: 1 }))),
    [reservations],
  );
  const commissionsSeries = useMemo(
    () => dailyBuckets(commissions.map(c => ({ date: undefined, value: Number(c.amount ?? 0) }))),
    [commissions],
  );

  const trendData = useMemo(() => revenueSeries.map((r, i) => ({
    date: r.date,
    label: shortDay(r.date),
    revenue: r.value,
    paid: paidSeries[i]?.value ?? 0,
  })), [revenueSeries, paidSeries]);

  const sourceData = useMemo(() => {
    const entries = Object.entries(summary?.bySource ?? {});
    return entries
      .filter(([, v]) => v.revenue > 0)
      .map(([name, v]) => ({ name: name || "Unknown", value: Number(v.revenue.toFixed(2)), count: v.count }))
      .sort((a, b) => b.value - a.value);
  }, [summary]);

  const reservationColumns = useMemo<ColumnDef<RevReservationRow, unknown>[]>(() => [
    { accessorKey: "guestName", header: "Guest", cell: info => <span className="font-medium">{String(info.getValue() ?? "")}</span> },
    {
      accessorFn: row => row.apartmentName || apartmentMap[Number(row.apartmentId)] || "-",
      id: "apartment",
      header: "Apartment",
    },
    {
      accessorKey: "checkIn",
      header: "Check-in",
      cell: info => {
        const v = info.getValue() as string | null | undefined;
        return v ? formatShortDate(v) : "-";
      },
    },
    {
      accessorKey: "checkOut",
      header: "Check-out",
      cell: info => {
        const v = info.getValue() as string | null | undefined;
        return v ? formatShortDate(v) : "-";
      },
    },
    {
      accessorKey: "totalPrice",
      header: "Total",
      cell: info => <span className="font-medium">{formatCurrency(Number(info.getValue() ?? 0))}</span>,
    },
    {
      accessorKey: "paidAmount",
      header: "Paid",
      cell: info => <span className="text-green-600 dark:text-green-400">{formatCurrency(Number(info.getValue() ?? 0))}</span>,
    },
    {
      accessorKey: "paymentStatus",
      header: "Payment",
      cell: info => <Badge variant="outline">{String(info.getValue() ?? "pending")}</Badge>,
    },
    { accessorKey: "source", header: "Source", cell: info => String(info.getValue() ?? "-") },
  ], [apartmentMap]);

  const commissionColumns = useMemo<ColumnDef<RevCommissionRow, unknown>[]>(() => [
    {
      accessorFn: row => row.beneficiaryName || userMap[Number(row.beneficiaryId)] || "-",
      id: "beneficiary",
      header: "Beneficiary",
      cell: info => <span className="font-medium">{String(info.getValue())}</span>,
    },
    {
      accessorKey: "beneficiaryType",
      header: "Type",
      cell: info => <Badge variant="outline">{String(info.getValue() ?? "-")}</Badge>,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: info => <span className="font-medium">{formatCurrency(Number(info.getValue() ?? 0))}</span>,
    },
    {
      accessorKey: "percentage",
      header: "Percentage",
      cell: info => `${info.getValue() ?? "0"}%`,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: info => <Badge variant={statusBadgeVariant(String(info.getValue()))}>{String(info.getValue())}</Badge>,
    },
  ], [userMap]);

  const reservationTotals = reservations.reduce(
    (acc, r) => ({
      total: acc.total + Number(r.totalPrice ?? 0),
      paid: acc.paid + Number(r.paidAmount ?? 0),
    }),
    { total: 0, paid: 0 },
  );

  const commissionTotal = commissions.reduce((s, c) => s + Number(c.amount ?? 0), 0);

  return (
    <div className="space-y-4">
      <MetricStrip cols={4}>
        <KpiCard
          label="Total Revenue"
          value={formatCurrency(Number(summary?.totalRevenue ?? 0))}
          helper="from bookings"
          icon={DollarSign}
          variant="success"
          sparkline={revenueSeries.map(b => b.value)}
          testId="stat-rev-total"
        />
        <KpiCard
          label="Paid"
          value={formatCurrency(Number(summary?.totalPaid ?? 0))}
          helper="collected"
          icon={Banknote}
          variant="info"
          sparkline={paidSeries.map(b => b.value)}
          testId="stat-rev-paid"
        />
        <KpiCard
          label="Commissions"
          value={formatCurrency(Number(summary?.totalCommissions ?? 0))}
          helper="earned"
          icon={TrendingUp}
          variant="warning"
          sparkline={commissionsSeries.map(b => b.value)}
          testId="stat-rev-commissions"
        />
        <KpiCard
          label="Reservations"
          value={summary?.totalReservations ?? 0}
          helper="in period"
          icon={CalendarDays}
          variant="default"
          sparkline={reservationsSeries.map(b => b.value)}
          testId="stat-rev-reservations"
        />
      </MetricStrip>

      <ChartCard
        title="Daily revenue"
        description="Total billed vs collected, last 30 days"
        height={240}
        isEmpty={trendData.every(d => d.revenue === 0 && d.paid === 0)}
        emptyText="No revenue in the last 30 days"
        testId="card-rev-trend"
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={trendData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="rev-total" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="rev-paid" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} width={48} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0)}`} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v: number) => formatCurrency(v)} />
            <Area type="monotone" dataKey="revenue" name="Billed" stroke="#10b981" fill="url(#rev-total)" strokeWidth={2} />
            <Area type="monotone" dataKey="paid" name="Paid" stroke="#3b82f6" fill="url(#rev-paid)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {sourceData.length > 0 && (
        <ChartCard
          title="Revenue by Source"
          description={`${sourceData.reduce((s, r) => s + r.count, 0)} reservations total`}
          isEmpty={sourceData.length === 0}
          emptyText="No revenue in period"
          height={240}
          testId="card-rev-source"
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={sourceData}
                dataKey="value"
                nameKey="name"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                label={({ name, percent }: { name?: string; percent?: number }) =>
                  `${name ?? ""} ${(((percent ?? 0) * 100)).toFixed(0)}%`
                }
                labelLine={false}
              >
                {sourceData.map((_, i) => (
                  <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v: number, _name: string, item: { payload?: { count?: number } }) =>
                  [`${formatCurrency(v)} · ${item?.payload?.count ?? 0} bookings`, "Revenue"]
                }
                contentStyle={{ fontSize: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            Reservations with Revenue
          </CardTitle>
          <Separator className="mt-3" />
        </CardHeader>
        <CardContent className="px-0 pb-2">
          <DataGrid
            data={reservations}
            columns={reservationColumns}
            searchPlaceholder="Search guest or apartment…"
            initialDensity="compact"
            showDensityToggle={false}
            exportFilename="revenue-reservations"
            getRowId={r => String(r.id)}
            emptyText="No revenue data in period"
            className="border-0 shadow-none rounded-none"
          />
          {reservations.length > 0 && (
            <div className="px-4 pt-2 pb-3 border-t flex flex-wrap gap-x-6 gap-y-1 text-xs">
              <span className="font-semibold">Total: {reservations.length} reservations</span>
              <span className="font-medium">{formatCurrency(reservationTotals.total)}</span>
              <span className="text-green-600 font-medium">
                {formatCurrency(reservationTotals.paid)} paid
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {commissions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <Banknote className="h-4 w-4 text-muted-foreground" />
              Commission Allocations
            </CardTitle>
            <Separator className="mt-3" />
          </CardHeader>
          <CardContent className="px-0 pb-2">
            <DataGrid
              data={commissions}
              columns={commissionColumns}
              searchPlaceholder="Search beneficiary…"
              initialDensity="compact"
              showDensityToggle={false}
              exportFilename="revenue-commissions"
              getRowId={r => String(r.id)}
              emptyText="No commissions in period"
              className="border-0 shadow-none rounded-none"
            />
            <div className="px-4 pt-2 pb-3 border-t flex flex-wrap gap-x-6 gap-y-1 text-xs">
              <span className="font-semibold">Total: {commissions.length} commissions</span>
              <span className="font-medium">{formatCurrency(commissionTotal)}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default RevenueTab;
